import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { EditUserDialog } from "@/pages/AdminExtra";
import { KYC_DOCS, KycDocsPanel } from "@/components/KycDocs";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, Card, Field, Select, Btn, DataTable, FilterBar, StatusBadge, txnColumns, ReceiptDialog } from "@/components/shared";

const PARENTS = { retailer: ["distributor", "sr", "super"], sr: ["distributor", "super"], distributor: ["super"], super: [] };

const call = async (fn, ok) => {
  try { await fn(); ok && toast.success(ok); return true; } catch (e) { toast.error(errMsg(e)); return false; }
};

export function AdminHome() {
  const [s, setS] = useState(null);
  const [pp, setPp] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data));
    api.get("/admin/payout/balance").then(({ data }) => setPp(data)).catch((e) => setPp({ error: errMsg(e) }));
  }, []);
  const tiles = [["Retailers", s?.retailers, "/admin/users"], ["Distributors", s?.distributors, "/admin/users"], ["Super Distributors", s?.supers, "/admin/users"], ["Sales Reps", s?.srs, "/admin/users"], ["Pending Approvals", s?.pending_approvals, "/admin/approvals"], ["Pending Fund Requests", s?.pending_funds, "/admin/fund-requests"], ["Open Tickets", s?.open_tickets, "/admin/tickets"], ["Pending DMT / Payout", s?.pending_txns, "/admin/transactions"]];
  return (
    <div>
      <PageHeader title="Admin Overview" subtitle="Operations snapshot across your whole network." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map(([l, v, to]) => (
          <Link key={l} to={to} className="np-card p-5 transition-colors hover:border-blue-300" data-testid={`admin-stat-${l.toLowerCase().replace(/\W+/g, "-")}`}>
            <div className="np-label">{l}</div><div className="font-display text-3xl font-bold">{v ?? "-"}</div>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title="ShubhPay payout wallet">
          <div data-testid="photonpe-balance">{!pp ? "Loading…" : pp.error ? <span className="text-sm text-red-600">{pp.error}</span> : (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-indigo-50 p-4"><div className="np-label">Balance</div><div className="font-mono text-xl font-semibold">{inr(pp.balance)}</div></div>
              <div className="rounded-lg bg-slate-50 p-4"><div className="np-label">Lien</div><div className="font-mono text-xl font-semibold">{inr(pp.lien)}</div></div>
            </div>)}
          </div>
        </Card>
        <Card title="Network wallet float">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-blue-50 p-4"><div className="np-label">Main</div><div className="font-mono text-xl font-semibold" data-testid="float-main">{inr(s?.float.main)}</div></div>
            <div className="rounded-lg bg-emerald-50 p-4"><div className="np-label">AEPS</div><div className="font-mono text-xl font-semibold" data-testid="float-aeps">{inr(s?.float.aeps)}</div></div>
          </div>
          <div className="mt-3 text-xs" data-testid="float-by-role">
            {[["retailer", "Retailers"], ["sr", "Sales Reps"], ["distributor", "Distributors"], ["super", "Super Distributors"]].map(([r, l]) => (
              <div key={r} className="flex justify-between border-b border-slate-100 py-1.5 last:border-0">
                <span className="text-slate-600">{l}</span>
                <span className="font-mono">{inr((s?.float_by_role?.[r]?.main || 0) + (s?.float_by_role?.[r]?.aeps || 0))}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1.5 font-semibold"><span>Total (Main + AEPS)</span><span className="font-mono" data-testid="float-total">{inr((s?.float.main || 0) + (s?.float.aeps || 0))}</span></div>
            {s?.float.locked > 0 && <div className="flex justify-between text-amber-700"><span>of which locked</span><span className="font-mono">{inr(s.float.locked)}</span></div>}
          </div>
        </Card>
        <Card title="Today's volume">
          {(s?.today || []).map((t) => <div key={t.service} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0"><span>{t.service} ×{t.count}</span><b className="font-mono">{inr(t.amount)}</b></div>)}
          {s && s.today.length === 0 && <div className="text-sm text-slate-400">No transactions today.</div>}
        </Card>
      </div>
    </div>
  );
}

function WalletDialog({ user, onClose, onDone }) {
  const [f, setF] = useState({ wallet: "main", type: "CR", amount: "", remarks: "" });
  const submit = async () => {
    if (await call(() => api.post(`/admin/users/${user.id}/wallet`, { ...f, amount: Number(f.amount) }), "Wallet updated")) { onDone(); onClose(); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wallet-adjust-dialog">
        <DialogHeader><DialogTitle className="font-display">Wallet adjustment · {user?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Wallet"><Select options={[{ value: "main", label: "Main" }, { value: "aeps", label: "AEPS" }]} value={f.wallet} onChange={(e) => setF({ ...f, wallet: e.target.value })} data-testid="adj-wallet-select" /></Field>
          <Field label="Type"><Select options={[{ value: "CR", label: "Credit" }, { value: "DR", label: "Debit" }]} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="adj-type-select" /></Field>
          <Field label="Amount"><input type="number" className="np-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="adj-amount-input" /></Field>
          <Field label="Remarks"><input className="np-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="adj-remarks-input" /></Field>
        </div>
        <Btn onClick={submit} data-testid="adj-submit-button">Apply</Btn>
      </DialogContent>
    </Dialog>
  );
}

function CreateUser({ open, onClose, onDone, role, distributors }) {
  const blank = { name: "", mobile: "", email: "", shop_name: "", city: "", state: "", password: "", mpin: "1234", parent_id: "" };
  const [f, setF] = useState(blank);
  const submit = async (e) => {
    e.preventDefault();
    if (await call(() => api.post("/admin/users", { ...f, role }), `${role === "distributor" ? "Distributor" : "Retailer"} created`)) { setF(blank); onDone(); onClose(); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-display">New {role === "super" ? "super distributor" : role}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {[["name", "Name"], ["mobile", "Mobile"], ["email", "Email"], ["shop_name", "Shop Name"], ["city", "City"], ["state", "State"], ["password", "Password"], ["mpin", "MPIN"]].map(([k, l]) => (
            <Field key={k} label={l}><input className="np-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required={["name", "mobile", "password"].includes(k)} data-testid={`new-user-${k}-input`} /></Field>
          ))}
          {role !== "super" && (
            <Field label="Under (parent)" className="col-span-2"><Select options={distributors.filter((d) => PARENTS[role].includes(d.role)).map((d) => ({ value: d.id, label: `${d.login_id} · ${d.name}` }))} placeholder="None (direct)" value={f.parent_id} onChange={(e) => setF({ ...f, parent_id: e.target.value })} data-testid="new-user-parent-select" /></Field>
          )}
          <Btn type="submit" className="col-span-2" data-testid="new-user-submit-button">Create</Btn>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const USER_ROLES = ["retailer", "sr", "distributor", "super"];

export function AdminUsers() {
  const params = useParams();
  const nav = useNavigate();
  const role = USER_ROLES.includes(params.role) ? params.role : "retailer";
  const setRole = (r) => nav(r === "retailer" ? "/admin/users" : `/admin/users/${r}`);
  const [rows, setRows] = useState([]);
  const [dts, setDts] = useState([]);
  const [adj, setAdj] = useState(null);
  const [create, setCreate] = useState(false);
  const [brand, setBrand] = useState(null);
  const [editU, setEditU] = useState(null);
  const [kycU, setKycU] = useState(null);
  const setLock = (u) => {
    const v = window.prompt(`Lock fund (minimum balance) for ${u.login_id}`, u.lock_fund || 0);
    if (v !== null) call(() => api.patch(`/admin/users/${u.id}/lock-fund`, { lock_fund: Number(v) }), "Lock fund updated").then(load);
  };
  const load = () => {
    const want = role;
    api.get("/admin/users", { params: { role: want } }).then(({ data }) => { if (want === roleRef.current) setRows(data); });
    Promise.all(["super", "distributor", "sr"].map((r) => api.get("/admin/users", { params: { role: r } })))
      .then((res) => setDts(res.flatMap((x) => x.data)));
  };
  const roleRef = useRef(role);
  useEffect(() => { roleRef.current = role; setRows([]); load(); }, [role]); // eslint-disable-line
  const toggle = (u) => call(() => api.patch(`/admin/users/${u.id}/status`, null, { params: { status: u.status === "active" ? "blocked" : "active" } }), "Status updated").then(load);
  const kyc = (u, status) => call(() => api.patch(`/admin/users/${u.id}/kyc`, null, { params: { status } }), "KYC updated").then(load);
  const resetPwd = (u) => {
    const pwd = window.prompt(`New password for ${u.login_id} (min 6 chars)`);
    if (pwd) call(() => api.post(`/admin/users/${u.id}/reset-password`, { password: pwd }), "Password reset");
  };
  const assign = (u, parent_id) => call(() => api.patch(`/admin/users/${u.id}/parent`, { parent_id: parent_id || null }), "Distributor assigned").then(load);
  const isRt = role === "retailer";
  return (
    <div>
      <PageHeader title={{ retailer: "Retailers", sr: "Sales Reps (SR)", distributor: "Distributors", super: "Super Distributors" }[role]}>
        <div className="flex gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {[["retailer", "Retailers"], ["sr", "Sales Reps (SR)"], ["distributor", "Distributors"], ["super", "Super Distributors"]].map(([r, l]) => (
              <button key={r} onClick={() => setRole(r)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${role === r ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`admin-role-tab-${r}`}>{l}</button>
            ))}
          </div>
          <Btn onClick={() => setCreate(true)} data-testid="create-retailer-button"><Plus className="h-4 w-4" />New {{ retailer: "Retailer", sr: "Sales Rep", distributor: "Distributor", super: "Super Distributor" }[role]}</Btn>
        </div>
      </PageHeader>
      <DataTable testid="admin-users-table" rows={rows} columns={[
        { label: "Login ID", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> }, { label: "Name", key: "name" },
        { label: "Mobile", key: "mobile" }, { label: "Shop", key: "shop_name" },
        { label: "Main", render: (r) => <span className="font-mono">{inr(r.balance)}</span> },
        ...(isRt ? [{ label: "AEPS", render: (r) => <span className="font-mono">{inr(r.aeps_balance)}</span> }] : []),
        { label: "Lock Fund", render: (r) => <button className="font-mono text-xs text-blue-700" onClick={() => setLock(r)} data-testid={`lock-fund-${r.login_id}`}>{inr(r.lock_fund)}</button> },
        ...(role !== "super" ? [
          { label: "Parent", render: (r) => <select className="rounded border border-slate-200 bg-white px-1 py-1 text-xs" value={r.parent_id || ""} onChange={(e) => assign(r, e.target.value)} data-testid={`assign-dt-${r.login_id}`}><option value="">Direct</option>{dts.filter((d) => PARENTS[role].includes(d.role)).map((d) => <option key={d.id} value={d.id}>{d.login_id}</option>)}</select> },
        ] : []),
        { label: "KYC", render: (r) => <div className="flex items-center gap-1"><StatusBadge status={r.kyc_status} /><button className="text-[11px] font-semibold text-blue-700" onClick={() => setKycU(r)} data-testid={`view-kyc-${r.login_id}`}>View ({KYC_DOCS.filter(([k]) => r.kyc_docs?.[k]).length}/{KYC_DOCS.length})</button>{r.kyc_status !== "VERIFIED" && <button className="text-[11px] font-semibold text-emerald-700" onClick={() => kyc(r, "VERIFIED")} data-testid={`verify-kyc-${r.login_id}`}>Verify</button>}</div> },
        { label: "Status", render: (r) => <span className={`text-xs font-bold uppercase ${r.status === "active" ? "text-emerald-700" : "text-red-600"}`}>{r.status}</span> },
        { label: "Actions", render: (r) => (
          <div className="flex gap-3 text-xs font-semibold">
            <button className="text-blue-700" onClick={() => setAdj(r)} data-testid={`adjust-wallet-${r.login_id}`}>Wallet</button>
            {!isRt && <button className="text-violet-700" onClick={() => setBrand(r)} data-testid={`branding-${r.login_id}`}>{r.branding?.logo_path ? "Logo ✓" : "Logo"}</button>}
            <button className="text-slate-700" onClick={() => setEditU(r)} data-testid={`edit-user-${r.login_id}`}>Edit</button>
            <button className="text-slate-700" onClick={() => resetPwd(r)} data-testid={`reset-password-${r.login_id}`}>Reset Pwd</button>
            <button className="text-red-600" onClick={() => toggle(r)} data-testid={`toggle-status-${r.login_id}`}>{r.status === "active" ? "Block" : "Unblock"}</button>
          </div>) },
      ]} />
      <WalletDialog user={adj} onClose={() => setAdj(null)} onDone={load} />
      <BrandingDialog user={brand} onClose={() => { setBrand(null); load(); }} />
      <EditUserDialog user={editU} onClose={() => { setEditU(null); load(); }} />
      <Dialog open={!!kycU} onOpenChange={(o) => { if (!o) { setKycU(null); load(); } }}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="member-kyc-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">KYC · {kycU?.login_id} {kycU?.name}</DialogTitle></DialogHeader>
          {kycU && <KycDocsPanel user={kycU} uploadUrl={`/admin/users/${kycU.id}/kyc-docs`} />}
        </DialogContent>
      </Dialog>
      <CreateUser open={create} onClose={() => setCreate(false)} onDone={load} role={role} distributors={dts} />
    </div>
  );
}

export function AdminFunds() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("PENDING");
  const load = () => api.get("/admin/fund-requests", { params: { status: status || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const decide = (r, action) => {
    const remarks = action === "reject" ? window.prompt("Reason for rejection?") || "" : "Approved";
    call(() => api.post(`/admin/fund-requests/${r.id}`, { action, remarks }), `Request ${action}d`).then(load);
  };
  return (
    <div>
      <PageHeader title="Fund Requests">
        <Select options={["PENDING", "APPROVED", "REJECTED", "CANCELLED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-fr-status-filter" />
      </PageHeader>
      <DataTable testid="admin-funds-table" rows={rows} columns={[
        { label: "Date", render: (r) => fmtDate(r.created_at) }, { label: "Req ID", render: (r) => <span className="font-mono text-xs">{r.req_id}</span> },
        { label: "Retailer", render: (r) => `${r.name} (${r.login_id})` }, { label: "Amount", render: (r) => <b className="font-mono">{inr(r.amount)}</b> },
        { label: "Mode", key: "payment_mode" }, { label: "UTR", key: "utr" }, { label: "Deposit Date", key: "deposit_date" },
        { label: "Slip", render: (r) => r.slip ? <a href={r.slip} download className="text-xs text-blue-700" data-testid={`slip-${r.req_id}`}>View</a> : "-" },
        { label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        { label: "Action", render: (r) => r.status === "PENDING" ? (
          <div className="flex gap-2">
            <Btn variant="green" className="h-8 px-3 text-xs" onClick={() => decide(r, "approve")} data-testid={`approve-fund-request-${r.req_id}`}>Approve</Btn>
            <Btn variant="outline" className="h-8 px-3 text-xs" onClick={() => decide(r, "reject")} data-testid={`reject-fund-request-${r.req_id}`}>Reject</Btn>
          </div>) : r.admin_remarks },
      ]} />
    </div>
  );
}

export function AdminTxns() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/admin/transactions", { params: { ...f, status: status || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const act = (r, action) => call(() => api.post(`/admin/transactions/${r.txn_id}/status`, { action, remarks: action === "refund" ? "Failed at bank" : "" }), "Updated").then(() => load());
  const cols = [{ label: "Retailer", key: "login_id" }, ...txnColumns(setRec), { label: "Resolve", render: (r) => r.status === "PENDING" && ["DMT", "Payout"].includes(r.service) ? (
    <div className="flex gap-2 text-xs font-semibold">
      <button className="text-emerald-700" onClick={() => act(r, "success")} data-testid={`mark-success-${r.txn_id}`}>Success</button>
      <button className="text-red-600" onClick={() => act(r, "refund")} data-testid={`mark-refund-${r.txn_id}`}>Refund</button>
    </div>) : "-" }];
  return (
    <div>
      <PageHeader title="All Transactions" />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="all-transactions" exportCols={cols.slice(0, 11)}>
        <Field label="Status"><Select options={["SUCCESS", "PENDING", "FAILED", "REFUNDED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-txn-status-filter" /></Field>
      </FilterBar>
      <DataTable testid="admin-txns-table" rows={rows} columns={cols} />
      <ReceiptDialog txn={rec} onClose={() => setRec(null)} />
    </div>
  );
}

export function AdminTickets() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/tickets").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const setStatus = (r, status) => call(() => api.patch(`/admin/tickets/${r.id}`, null, { params: { status } }), "Ticket updated").then(load);
  return (
    <div>
      <PageHeader title="Ticket Desk" />
      <DataTable testid="admin-tickets-table" rows={rows} columns={[
        { label: "Date", render: (r) => fmtDate(r.created_at) }, { label: "Ticket", render: (r) => <Link to={`/support/${r.id}`} className="font-mono text-xs text-blue-700" data-testid={`open-ticket-${r.ticket_no}`}>{r.ticket_no}</Link> },
        { label: "Retailer", render: (r) => `${r.name} (${r.login_id})` }, { label: "Subject", key: "subject" }, { label: "Service", key: "service" },
        { label: "Priority", key: "priority" }, { label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        { label: "Action", render: (r) => r.status !== "CLOSED" ? <button className="text-xs font-semibold text-slate-700" onClick={() => setStatus(r, "CLOSED")} data-testid={`close-ticket-${r.ticket_no}`}>Close</button> : "-" },
      ]} />
    </div>
  );
}

export function AdminNews() {
  const [rows, setRows] = useState([]);
  const [news, setNews] = useState("");
  const load = () => api.get("/admin/news").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (await call(() => api.post("/admin/news", { news, audience: "RT" }), "News published")) { setNews(""); load(); }
  };
  return (
    <div>
      <PageHeader title="Broadcast News" subtitle="Shown on every retailer's dashboard ticker." />
      <Card className="mb-4">
        <form onSubmit={add} className="flex gap-2" data-testid="news-form">
          <input className="np-input" value={news} onChange={(e) => setNews(e.target.value)} placeholder="Announcement text" required data-testid="news-input" />
          <Btn type="submit" data-testid="news-submit-button">Publish</Btn>
        </form>
      </Card>
      <div className="space-y-2">
        {rows.map((n) => (
          <div key={n.id} className="np-card flex items-center justify-between p-3 text-sm" data-testid={`news-item-${n.id}`}>
            <div>{n.news}<div className="text-[11px] text-slate-400">{fmtDate(n.created_at)}</div></div>
            <button onClick={() => call(() => api.delete(`/admin/news/${n.id}`), "Deleted").then(load)} className="text-slate-400 hover:text-red-600" data-testid={`delete-news-${n.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminCallbacks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/admin/callback-logs").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setLoading(true);
    try { const { data } = await api.post("/admin/payout/sync"); toast.success(`Checked ${data.checked} pending payouts`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const url = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/callback/photonpe/payout`;
  return (
    <div>
      <PageHeader title="Payout Callbacks" subtitle="ShubhPay callbacks are logged here. Each callback triggers a status check; pending payouts are also re-checked every 2 minutes.">
        <Btn loading={loading} onClick={sync} data-testid="payout-sync-now-button">Check all pending now</Btn>
      </PageHeader>
      <Card className="mb-4"><div className="np-label">Callback URL (share with ShubhPay)</div><div className="break-all font-mono text-sm" data-testid="callback-url">{url}</div></Card>
      <DataTable testid="callback-logs-table" rows={rows} columns={[
        { label: "Time", render: (r) => fmtDate(r.created_at) }, { label: "Txn", render: (r) => <span className="font-mono text-xs">{r.txn_id || "-"}</span> },
        { label: "Result", render: (r) => <StatusBadge status={r.result} /> }, { label: "IP", key: "ip" },
        { label: "Payload", render: (r) => <span title={JSON.stringify(r.payload)} className="font-mono text-[11px] text-slate-500">{JSON.stringify(r.payload).slice(0, 120)}</span> },
      ]} />
    </div>
  );
}

const ROLE_TABS = [["retailer", "Retailers"], ["sr", "Sales Reps"], ["distributor", "Distributors"], ["super", "Super Distributors"]];

function RoleTabs({ role, setRole, prefix }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
      {ROLE_TABS.map(([r, l]) => (
        <button key={r} onClick={() => setRole(r)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${role === r ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`${prefix}-role-${r}`}>{l}</button>
      ))}
    </div>
  );
}

function RoleServices({ catalog, roles, reload }) {
  const [role, setRole] = useState("retailer");
  const toggle = (k, v) => call(() => api.put(`/admin/services/role/${role}`, { services: { [k]: v } }), `${catalog[k]} ${v ? "ON" : "OFF"} for all ${role}s`).then(reload);
  return (
    <Card title="Role-wise services" action={<RoleTabs role={role} setRole={setRole} prefix="svc" />}>
      <div className="text-xs text-slate-500">Applies to every user of this role unless set differently for a user below.</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="role-services-list">
        {Object.entries(catalog).map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm">
            <span className="font-medium">{l}</span>
            <Switch checked={!!roles[role]?.[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`role-svc-${role}-${k}`} />
          </label>
        ))}
      </div>
    </Card>
  );
}

function UserServicesDialog({ user, catalog, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (user) api.get(`/admin/services/user/${user.id}`).then(({ data }) => setD(data)); else setD(null); }, [user]);
  const change = async (k, val) => {
    const v = val === "default" ? null : val === "on";
    try { const { data } = await api.put(`/admin/services/user/${user.id}`, { services: { [k]: v } }); setD(data); toast.success(`${catalog[k]} updated`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-services-dialog">
        <DialogHeader><DialogTitle className="font-display">Services · {user?.name} ({user?.login_id})</DialogTitle></DialogHeader>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : (
          <div className="space-y-2">
            {Object.entries(catalog).map(([k, l]) => {
              const cur = k in d.overrides ? (d.overrides[k] ? "on" : "off") : "default";
              return (
                <div key={k} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 text-sm last:border-0">
                  <div><div className="font-medium">{l}</div><div className={`text-[11px] font-bold ${d.effective[k] ? "text-emerald-700" : "text-red-600"}`}>{d.effective[k] ? "ACTIVE" : "OFF"}</div></div>
                  <select className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs" value={cur} onChange={(e) => change(k, e.target.value)} data-testid={`user-svc-${k}`}>
                    <option value="default">Role default ({d.role_defaults[k] ? "ON" : "OFF"})</option>
                    <option value="on">ON</option>
                    <option value="off">OFF</option>
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminServices() {
  const [cfg, setCfg] = useState(null);
  const [role, setRole] = useState("retailer");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const reload = () => api.get("/admin/services").then(({ data }) => setCfg(data));
  useEffect(() => { reload(); }, []);
  useEffect(() => { api.get("/admin/users", { params: { role } }).then(({ data }) => setUsers(data)); }, [role]);
  const shown = users.filter((u) => !q || `${u.login_id} ${u.name} ${u.mobile}`.toLowerCase().includes(q.toLowerCase()));
  if (!cfg) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <PageHeader title="Services" subtitle="Turn services ON or OFF for a whole role, then change it for any single user. A service turned OFF disappears from that user's menu." />
      <RoleServices catalog={cfg.catalog} roles={cfg.roles} reload={reload} />
      <Card className="mt-6" title="User-wise services" action={<RoleTabs role={role} setRole={setRole} prefix="svc-user" />}>
        <input className="np-input mb-3 max-w-sm" placeholder="Search login ID, name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="svc-user-search" />
        <DataTable testid="svc-users-table" rows={shown} columns={[
          { label: "Login ID", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> }, { label: "Name", key: "name" }, { label: "Mobile", key: "mobile" },
          { label: "Custom", render: (r) => { const n = Object.keys(r.services || {}).length; return n ? <span className="text-xs font-semibold text-blue-700">{n} custom</span> : <span className="text-xs text-slate-400">Role default</span>; } },
          { label: "Action", render: (r) => <button className="text-xs font-semibold text-blue-700" onClick={() => setSel(r)} data-testid={`manage-services-${r.login_id}`}>Manage</button> },
        ]} />
      </Card>
      <UserServicesDialog user={sel} catalog={cfg.catalog} onClose={() => { setSel(null); api.get("/admin/users", { params: { role } }).then(({ data }) => setUsers(data)); }} />
    </div>
  );
}

function BrandingDialog({ user, onClose }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const b = user?.branding;
  useEffect(() => { setName(b?.company_name || user?.shop_name || ""); setFile(null); }, [user]); // eslint-disable-line
  const preview = file ? URL.createObjectURL(file) : b?.logo_path ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/branding/logo/${user.id}?v=${b.updated_at}` : null;
  const save = async () => {
    const fd = new FormData();
    fd.append("company_name", name);
    if (file) fd.append("file", file);
    setSaving(true);
    if (await call(() => api.post(`/admin/users/${user.id}/branding`, fd), "Logo saved — visible to the whole network")) onClose();
    setSaving(false);
  };
  const remove = async () => { if (await call(() => api.delete(`/admin/users/${user.id}/branding`), "Logo removed")) onClose(); };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="branding-dialog">
        <DialogHeader><DialogTitle className="font-display">Logo · {user?.name} ({user?.login_id})</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">This logo and company name will show in the panel of this user and everyone mapped under them.</div>
        <div className="grid h-28 place-items-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
          {preview ? <img src={preview} alt="logo preview" className="max-h-24 max-w-[220px] object-contain" data-testid="branding-preview" /> : <span className="text-xs text-slate-400">No logo yet</span>}
        </div>
        <Field label="Logo (PNG / JPG / WEBP, max 2 MB)"><input type="file" accept="image/png,image/jpeg,image/webp" className="np-input" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="branding-file-input" /></Field>
        <Field label="Company name"><input className="np-input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name-input" /></Field>
        <div className="flex justify-between gap-2">
          {b?.logo_path ? <Btn variant="outline" onClick={remove} data-testid="branding-remove-button">Remove logo</Btn> : <span />}
          <Btn onClick={save} loading={saving} disabled={!name.trim() || (!file && !b?.logo_path)} data-testid="branding-save-button">Save</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
